// "When I'm not active on desktop, send to my phone after X minutes" (#683). Shared by the room (who gets a push now, who
// has it held) and by the clients (the setting, and how a desktop client reports input). Cases for the native ports:
// shared/cases/idle-push.json.
//
// A person's devices are two kinds. A desktop device is a browser's Web Push on a computer or a Mac app's APNs token. A
// mobile device is an iPhone's APNs token, an Android FCM token, or a browser's Web Push on a phone. The setting only
// decides what happens to the mobile ones while a desktop is in use.

/** Which kind a push device is. */
export type DeviceForm = "desktop" | "mobile";
export const DEVICE_FORMS: readonly DeviceForm[] = ["desktop", "mobile"];
export const isDeviceForm = (v: unknown): v is DeviceForm => v === "desktop" || v === "mobile";

/** The APNs rows' platform: the iOS app on a phone or iPad (mobile) or on a Mac (desktop). */
export type ApnsPlatform = "ios" | "macos";
export const isApnsPlatform = (v: unknown): v is ApnsPlatform => v === "ios" || v === "macos";
export const formOfApnsPlatform = (p: ApnsPlatform): DeviceForm => (p === "macos" ? "desktop" : "mobile");

/** The setting's value for "Never": a phone gets nothing while a desktop is active (before #683's idle check). */
export const IDLE_PUSH_NEVER = -1;
/** The default, as in Slack: 5 minutes. */
export const DEFAULT_IDLE_PUSH_MINUTES = 5;

/** The choices, in the order the setting lists them. 0 is "Right away". */
export const IDLE_PUSH_CHOICES: readonly { minutes: number; label: string }[] = [
  { minutes: 0, label: "Right away" },
  { minutes: 1, label: "After 1 minute" },
  { minutes: 2, label: "After 2 minutes" },
  { minutes: 5, label: "After 5 minutes" },
  { minutes: 10, label: "After 10 minutes" },
  { minutes: 15, label: "After 15 minutes" },
  { minutes: 30, label: "After 30 minutes" },
  { minutes: IDLE_PUSH_NEVER, label: "Never" },
];
export const isIdlePushMinutes = (v: unknown): v is number => typeof v === "number" && IDLE_PUSH_CHOICES.some((c) => c.minutes === v);
/** A stored or received value as a choice; anything else reads as the default. */
export const idlePushMinutesOr = (v: unknown): number => (isIdlePushMinutes(v) ? v : DEFAULT_IDLE_PUSH_MINUTES);
export const idlePushLabel = (minutes: number): string => (IDLE_PUSH_CHOICES.find((c) => c.minutes === minutes) ?? IDLE_PUSH_CHOICES[3]!).label;

/**
 * A desktop client tells the room about input at most this often (ms) while there is input, and when its state
 * changes. The room therefore sees a `lastInputAt` at most this old while the person types.
 */
export const IDLE_REPORT_MS = 30_000;

/**
 * What the room knows about one open socket. `lastInputAt` (Unix ms, the client's clock) is sent by desktop clients
 * only. A socket without it (iOS, Android, an older client, a phone's browser) is active for as long as it says so.
 */
export interface SocketPresence {
  active: boolean;
  lastInputAt?: number;
}

/**
 * What to do with a message's push for one person, once the rules (levels, mutes, mentions) say they get it:
 *   now "all"    every device now (they are not active anywhere)
 *   now "mobile" their mobile devices now; the desktop is in use, so desktop devices stay quiet
 *   now "none"   no device now
 *   holdUntil    their mobile devices get it then, if the message is still unread
 */
export interface PushPlan {
  now: "all" | "mobile" | "none";
  holdUntil?: number;
}

/**
 * Applies the setting to one person's open sockets.
 * - Nobody active: everything now.
 * - "Never", or an active socket that sends no `lastInputAt` (a phone in front, an older client): nothing, as before.
 * - An active desktop (sends `lastInputAt`) counts as in use if its last input is under `minutes` old. "Right away"
 *   has no age limit: the desktop devices stay quiet and the mobile ones get the push at once.
 * - In use: mobile devices wait `minutes` and then get it if still unread.
 * - Active desktops that have been idle for `minutes` or more: as if nobody is active.
 */
export function planPush(sockets: readonly SocketPresence[], minutes: number, now: number): PushPlan {
  const active = sockets.filter((s) => s.active);
  if (!active.length) return { now: "all" };
  if (minutes === IDLE_PUSH_NEVER || active.some((s) => s.lastInputAt === undefined)) return { now: "none" };
  if (minutes <= 0) return { now: "mobile" };
  const limit = minutes * 60_000;
  // A time ahead of ours is clock skew, not the future: it counts as now.
  const inUse = active.some((s) => now - Math.min(s.lastInputAt!, now) < limit);
  return inUse ? { now: "none", holdUntil: now + limit } : { now: "all" };
}

/**
 * What to do with a held push when it falls due (#683). The person may have kept working since it was held.
 *   send   nobody is active, or every active desktop has been idle for `minutes`: the phone gets it (if still unread)
 *   wait   an active desktop had input after the hold began: look again at `until`, when it will have been idle that long
 *   drop   "Never", or a device in front that sends no `lastInputAt` (a phone in use): the phone does not get it
 * "Right away" has nothing to wait for, so it sends.
 */
export type DuePlan = { do: "send" } | { do: "wait"; until: number } | { do: "drop" };

export function duePlan(sockets: readonly SocketPresence[], minutes: number, now: number): DuePlan {
  const active = sockets.filter((s) => s.active);
  if (!active.length) return { do: "send" };
  if (minutes === IDLE_PUSH_NEVER || active.some((s) => s.lastInputAt === undefined)) return { do: "drop" };
  if (minutes <= 0) return { do: "send" };
  const limit = minutes * 60_000;
  const until = Math.max(...active.map((s) => Math.min(s.lastInputAt!, now) + limit));
  return until > now ? { do: "wait", until } : { do: "send" };
}

/**
 * The idle report of a desktop client. It tracks input and whether the app is in front, and says when to send
 * `{t: "active", active, lastInputAt}`: at once when the state changes, and while there is input at most every
 * IDLE_REPORT_MS. Plain data and time passed in, so the web and the Mac port share its cases.
 */
export interface IdleReporter {
  front: boolean;
  lastInputAt: number | null;
  sentAt: number | null;
  sentFront: boolean | null;
  sentInputAt: number | null;
}
export const newIdleReporter = (): IdleReporter => ({ front: false, lastInputAt: null, sentAt: null, sentFront: null, sentInputAt: null });

/** The app came to the front or left it. Coming to the front counts as input: the person just did something. */
export function idleSetFront(r: IdleReporter, front: boolean, now: number): void {
  if (front && !r.front) r.lastInputAt = now;
  r.front = front;
}

/** Input happened (a key, the pointer, a scroll, a touch; on the Mac, the system's idle time reset). */
export function idleInput(r: IdleReporter, at: number): void {
  if (r.lastInputAt === null || at > r.lastInputAt) r.lastInputAt = at;
}

/** The frame to send now, or null. Call after every change to `front` or `lastInputAt`, and from a timer. */
export function nextIdleReport(r: IdleReporter, now: number): { active: boolean; lastInputAt: number } | null {
  const lastInputAt = r.lastInputAt ?? now;
  if (r.sentFront === null || r.sentFront !== r.front) return { active: r.front, lastInputAt };
  if (!r.front) return null;
  // Same state in front: only newer input is news, and not more often than the interval.
  if (r.lastInputAt === null || r.lastInputAt === r.sentInputAt) return null;
  if (r.sentAt !== null && now - r.sentAt < IDLE_REPORT_MS) return null;
  return { active: true, lastInputAt };
}

/** Records that `frame` went out. */
export function idleReportSent(r: IdleReporter, frame: { active: boolean; lastInputAt: number }, now: number): void {
  r.sentAt = now;
  r.sentFront = frame.active;
  r.sentInputAt = frame.lastInputAt;
}
